import { Link } from "react-router-dom";
import { ND, eur, eurShort, isND, num, perc } from "@/lib/format";
import { TrendingDown, TrendingUp, Minus, Info } from "lucide-react";

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white border border-slate-200 shadow-sm rounded-lg ${className}`} {...rest}>
    {children}
  </div>
);

/** Link alla home dei canali, sempre visibile (anche su mobile), in testa alle pagine di apertura. */
export const TornaHome = ({ testId = "btn-torna-home" }) => (
  <Link to="/scelta-canale" data-testid={testId}
    className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 mb-1
      transition-colors hover:text-slate-900">
    ← Torna alla home
  </Link>
);

export const SectionTitle = ({ children, sub, right }) => (
  <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
    <div>
      <h2 className="text-xl sm:text-2xl tracking-tight font-semibold text-slate-900">{children}</h2>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>
    {right}
  </div>
);

export const NdBadge = ({ label = "dato non disponibile" }) => (
  <span className="inline-flex items-center gap-1 text-xs text-slate-400 border border-dashed border-slate-300 rounded px-1.5 py-0.5">
    <Info size={12} /> {label}
  </span>
);

export function VarianceIndicator({ value, suffix = "", testId }) {
  if (isND(value)) return <NdBadge />;
  const pos = value >= 0;
  const Icon = value === 0 ? Minus : pos ? TrendingUp : TrendingDown;
  // semaforo scostamenti: verde da 0 in su, giallo fino a -10%, rosso oltre
  const cls = value === 0 ? "text-slate-500"
    : pos ? "text-emerald-600" : value >= -10 ? "text-amber-600" : "text-red-600";
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 text-sm font-medium font-mono ${cls}`}>
      <Icon size={14} /> {perc(value)}{suffix}
    </span>
  );
}

/** Sottotitolo del box Rischio: scostamento dal target, verde se sotto (in obiettivo), rosso se sopra. */
export function ScostamentoRischio({ rischio, target }) {
  if (rischio == null) return "da inserire in Configurazioni";
  if (target == null) return "target non impostato";
  const delta = rischio - target;
  const ok = delta <= 0;
  const perc = target ? (delta / target) * 100 : null;
  return (
    <span data-testid="rischio-scostamento">
      target {num(target, 2)}% ·{" "}
      <span className={`font-mono font-semibold ${ok ? "text-emerald-600" : "text-red-600"}`}>
        {perc == null ? `${delta > 0 ? "+" : ""}${num(delta, 2)} pt` : `${perc > 0 ? "+" : ""}${num(perc, 1)}%`}
        {ok ? " · in obiettivo" : " · sopra target"}
      </span>
    </span>
  );
}

export function KpiCard({ label, value, kind = "num", sub, variance, accent = "slate",
  onClick, testId, note }) {
  const accents = {
    liquidato: "txt-liquidato", caricato: "txt-caricato", positivo: "text-emerald-600",
    negativo: "text-red-600", warning: "text-amber-600", slate: "text-slate-900",
    sky: "text-sky-600", skyLight: "text-sky-400", caricatoLight: "txt-caricato",
    previsione: "text-teal-600",
  };
  const backgrounds = {
    liquidato: "!border-2 bd-liquidato soft-liquidato",
    positivo: "!border-2 !border-emerald-600 bg-gradient-to-b from-emerald-50/70 to-white",
    caricato: "!border-2 bd-caricato soft-caricato",
    negativo: "!border-2 !border-red-600 bg-gradient-to-b from-red-50/70 to-white",
    warning: "!border-2 !border-orange-500 bg-gradient-to-b from-orange-50 to-white",
    sky: "!border-2 !border-sky-600 bg-gradient-to-b from-sky-50/70 to-white",
    skyLight: "!border-2 !border-sky-300 bg-gradient-to-b from-sky-50/50 to-white",
    caricatoLight: "!border-2 bd-caricato-light soft-caricato",
    previsione: "!border-2 !border-teal-400 bg-gradient-to-b from-teal-50/60 to-white",
    slate: "",
  };
  const fmt = kind === "eur" ? eur : kind === "eurShort" ? eurShort : kind === "perc" ? perc
    : kind === "quota" ? (v) => `${num(v, 2)}%` : kind === "text" ? (v) => v : num;
  const nd = isND(value);
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      className={`p-6 h-full flex flex-col justify-between transition-transform duration-200 ${backgrounds[accent]} ${onClick ? "cursor-pointer hover:-translate-y-[1px] hover:shadow-md" : ""}`}
    >
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className="mt-3">
        {nd ? (
          <NdBadge />
        ) : (
          <div className={`font-mono text-xl sm:text-2xl font-semibold tracking-tight whitespace-nowrap ${accents[accent]}`}>
            {fmt(value)}
          </div>
        )}
        {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
        {variance !== undefined && <div className="mt-2"><VarianceIndicator value={variance} /></div>}
        {note && <div className="text-xs text-slate-400 mt-2">{note}</div>}
      </div>
    </Card>
  );
}

export function BudgetProgress({ blocco, testId }) {
  const { budget_importo, budget_numero, proiezione_importo, proiezione_numero,
    scostamento_perc, scostamento_numero_perc, stato, categoria, tipo_kpi, budget_da_validare } = blocco;
  const usaImporto = !isND(budget_importo);
  const budget = usaImporto ? budget_importo : budget_numero;
  const proj = usaImporto ? proiezione_importo : proiezione_numero;
  const sc = usaImporto ? scostamento_perc : scostamento_numero_perc;
  const ratio = budget ? Math.min(200, Math.max(0, ((proj || 0) / budget) * 100)) : 0;
  const barColor = stato === "positivo" ? "bg-emerald-500" : stato === "warning" ? "bg-amber-500" : stato === "negativo" ? "bg-red-500" : "bg-slate-300";
  const fmt = usaImporto ? eurShort : (v) => num(v);
  return (
    <Card className="p-6" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
          Budget {tipo_kpi.toLowerCase()} · {categoria}
        </div>
        <VarianceIndicator value={sc} />
      </div>
      {isND(budget) ? (
        <div className="mt-4"><NdBadge label="budget non impostato" /></div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-slate-500">Proiettato</div>
              <div className="font-mono text-lg font-semibold text-slate-900">{fmt(proj)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Budget {budget_da_validare && "(da validare)"}</div>
              <div className="font-mono text-lg font-semibold text-slate-500">{fmt(budget)}</div>
            </div>
          </div>
          <div className="mt-4 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${barColor} transition-[width] duration-500`} style={{ width: `${Math.min(100, ratio)}%` }} />
          </div>
          <div className="mt-2 text-xs text-slate-500 font-mono">
            {num(ratio, 2)}% del budget · scostamento {usaImporto ? eurShort(blocco.scostamento_importo) : num(blocco.scostamento_numero)}
          </div>
        </>
      )}
    </Card>
  );
}

export function EmptyState({ title, description }) {
  return (
    <Card className="p-10 text-center">
      <div className="text-slate-900 font-semibold">{title}</div>
      {description && <p className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">{description}</p>}
    </Card>
  );
}
